import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackEvent, getCurrentPage } from '../services/analytics';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [feedbackSettings, setFeedbackSettings] = useState({
    showFloatingButton: true,
    showPageWidgets: true,
    showExitIntent: true,
    feedbackFrequency: 'normal', // 'minimal', 'normal', 'frequent'
    collectAnalytics: true,
    userConsent: null // null, true, false
  });

  const [feedbackQueue, setFeedbackQueue] = useState([]);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({
    totalSubmitted: 0,
    lastSubmissionDate: null,
    mostCommonType: null
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('fers_feedback_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setFeedbackSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('[FeedbackContext] Error loading settings:', error);
      }
    }

    const savedHistory = localStorage.getItem('fers_feedback_history');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        setFeedbackHistory(parsed);
        
        // Calculate stats
        const stats = calculateFeedbackStats(parsed);
        setFeedbackStats(stats);
      } catch (error) {
        console.error('[FeedbackContext] Error loading history:', error);
      }
    }
  }, []);

  // Save settings to localStorage when changed
  useEffect(() => {
    localStorage.setItem('fers_feedback_settings', JSON.stringify(feedbackSettings));
  }, [feedbackSettings]);

  // Save history to localStorage when changed
  useEffect(() => {
    localStorage.setItem('fers_feedback_history', JSON.stringify(feedbackHistory));
    
    const stats = calculateFeedbackStats(feedbackHistory);
    setFeedbackStats(stats);
  }, [feedbackHistory]);

  const calculateFeedbackStats = (history) => {
    if (!history.length) {
      return {
        totalSubmitted: 0,
        lastSubmissionDate: null,
        mostCommonType: null
      };
    }

    const typeCount = {};
    let lastDate = null;

    history.forEach(item => {
      // Count feedback types
      if (item.feedbackType) {
        typeCount[item.feedbackType] = (typeCount[item.feedbackType] || 0) + 1;
      }
      
      // Track latest date
      if (item.timestamp && (!lastDate || new Date(item.timestamp) > new Date(lastDate))) {
        lastDate = item.timestamp;
      }
    });

    // Find most common type
    const mostCommonType = Object.keys(typeCount).reduce((a, b) => 
      typeCount[a] > typeCount[b] ? a : b, null
    );

    return {
      totalSubmitted: history.length,
      lastSubmissionDate: lastDate,
      mostCommonType
    };
  };

  const updateSettings = (newSettings) => {
    setFeedbackSettings(prev => ({
      ...prev,
      ...newSettings
    }));

    trackEvent('feedback_settings_changed', {
      settings: Object.keys(newSettings),
      page: getCurrentPage()
    });
  };

  const addToQueue = (feedbackItem) => {
    const queueItem = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      page: getCurrentPage(),
      ...feedbackItem
    };

    setFeedbackQueue(prev => [...prev, queueItem]);
    
    trackEvent('feedback_queued', {
      feedback_type: feedbackItem.feedbackType,
      queue_length: feedbackQueue.length + 1
    });

    return queueItem.id;
  };

  const removeFromQueue = (itemId) => {
    setFeedbackQueue(prev => prev.filter(item => item.id !== itemId));
  };

  const addToHistory = (feedbackItem) => {
    const historyItem = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      page: getCurrentPage(),
      ...feedbackItem
    };

    setFeedbackHistory(prev => [...prev, historyItem]);
    
    return historyItem.id;
  };

  const clearHistory = () => {
    setFeedbackHistory([]);
    setFeedbackStats({
      totalSubmitted: 0,
      lastSubmissionDate: null,
      mostCommonType: null
    });
    
    trackEvent('feedback_history_cleared');
  };

  const shouldShowFeedbackWidget = (widgetType, page = getCurrentPage()) => {
    if (!feedbackSettings.collectAnalytics) return false;

    const { feedbackFrequency } = feedbackSettings;
    const recentFeedback = feedbackHistory.filter(
      item => item.page === page && 
      new Date(item.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000) // Last 24 hours
    );

    switch (feedbackFrequency) {
      case 'minimal':
        return recentFeedback.length === 0;
      case 'normal':
        return recentFeedback.length < 2;
      case 'frequent':
        return recentFeedback.length < 5;
      default:
        return true;
    }
  };

  const getFeedbackSummary = () => {
    const last30Days = feedbackHistory.filter(
      item => new Date(item.timestamp) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );

    const byType = {};
    const byPage = {};
    const byRating = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    last30Days.forEach(item => {
      if (item.feedbackType) {
        byType[item.feedbackType] = (byType[item.feedbackType] || 0) + 1;
      }
      if (item.page) {
        byPage[item.page] = (byPage[item.page] || 0) + 1;
      }
      if (item.rating && item.rating >= 1 && item.rating <= 5) {
        byRating[item.rating]++;
      }
    });

    return {
      totalLast30Days: last30Days.length,
      byType,
      byPage,
      byRating,
      averageRating: calculateAverageRating(last30Days)
    };
  };

  const calculateAverageRating = (feedbackItems) => {
    const ratingsOnly = feedbackItems
      .filter(item => item.rating && item.rating >= 1 && item.rating <= 5)
      .map(item => item.rating);

    if (ratingsOnly.length === 0) return null;

    const sum = ratingsOnly.reduce((acc, rating) => acc + rating, 0);
    return Math.round((sum / ratingsOnly.length) * 10) / 10; // Round to 1 decimal
  };

  const exportFeedbackData = () => {
    const exportData = {
      settings: feedbackSettings,
      history: feedbackHistory,
      stats: feedbackStats,
      summary: getFeedbackSummary(),
      exportedAt: new Date().toISOString()
    };

    trackEvent('feedback_data_exported', {
      history_count: feedbackHistory.length,
      settings_count: Object.keys(feedbackSettings).length
    });

    return exportData;
  };

  const value = {
    // Settings
    feedbackSettings,
    updateSettings,
    
    // Queue management
    feedbackQueue,
    addToQueue,
    removeFromQueue,
    
    // History management
    feedbackHistory,
    addToHistory,
    clearHistory,
    
    // Stats and analytics
    feedbackStats,
    getFeedbackSummary,
    
    // Utility functions
    shouldShowFeedbackWidget,
    exportFeedbackData
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};

export default FeedbackContext;